import { PagePermissionEntityInterface, RoleEntityInterface } from '@types';
import { roleActionTypes } from '@actions/actionTypes/role';
import {
  addRole,
  editRole,
  getAllRoles,
  updatePermissions,
  deleteRole,
} from '@services/roleService';
import { setNotification } from '@actions/appActions';
import _cloneDeep from 'lodash/cloneDeep';
import { loading } from '@actions/commonActions';
import { setIsOpenRoleDeleteModal } from '@actions/rolesPageActions';

export const setRoles = (roles: RoleEntityInterface[]) => {
  return {
    type: roleActionTypes.SET_ROLES,
    payload: roles,
  };
};

export const setRoleIdForDelete = (id: string) => {
  return {
    type: roleActionTypes.SET_ROLE_ID_FOR_DELETE,
    payload: id,
  };
};

export const makeRoles = () => {
  return async (dispatch) => {
    try {
      const { items } = await getAllRoles();
      dispatch(setRoles(items));
    } catch (err) {
      dispatch(setNotification({ textKey: 'error', variant: 'error' }));
    }
  };
};

export const createRole = (data: Partial<RoleEntityInterface>) => {
  return async (dispatch) => {
    try {
      dispatch(setCurrentRolePermissionIsSaving(true));
      const { id } = await addRole(data);
      await updatePermissions(id, data.pagePermissions);
      dispatch(setCurrentRolePermissions(null));
      dispatch(makeRoles());
      dispatch(setCurrentRolePermissionIsSaving(false));
      dispatch(setNotification({ textKey: 'role_created', variant: 'success' }));
      return true;
    } catch (err) {
      dispatch(
        setNotification({
          textKey: 'role_already_exist',
          variant: 'error',
          additionalText: data.name,
        })
      );
      dispatch(setCurrentRolePermissionIsSaving(false));
      return false;
    }
  };
};

export const makeDeleteRole = (id: string) => {
  const instanceName = 'deleting_role';

  return async (dispatch) => {
    try {
      dispatch(loading(true, instanceName));
      await deleteRole(id);
      dispatch(makeRoles());
      dispatch(setNotification({ textKey: 'role_success_deleted', variant: 'success' }));
    } catch (err) {
      dispatch(
        setNotification({
          textKey: 'role_error_deleted',
          variant: 'error',
        })
      );
    } finally {
      dispatch(setIsOpenRoleDeleteModal(false));
      dispatch(setRoleIdForDelete(null));
      dispatch(loading(false, instanceName));
    }
  };
};

export const updateRole = (data: Partial<RoleEntityInterface>) => {
  return async (dispatch) => {
    try {
      dispatch(setCurrentRolePermissionIsSaving(true));
      await editRole(data);
      await updatePermissions(data.id, data.pagePermissions);
      dispatch(setCurrentRolePermissions(null));
      dispatch(makeRoles());
      dispatch(setCurrentRolePermissionIsSaving(false));
      dispatch(setNotification({ textKey: 'role_edited', variant: 'success' }));
      return true;
    } catch (err) {
      dispatch(
        setNotification({
          textKey: 'role_already_exist',
          variant: 'error',
          additionalText: data.name,
        })
      );
      dispatch(setCurrentRolePermissionIsSaving(false));
      return false;
    }
  };
};

export const setCurrentRolePermissions = (pagePermissions: PagePermissionEntityInterface[]) => {
  return {
    type: roleActionTypes.SET_CURRENT_ROLE_PERMISSIONS,
    payload: _cloneDeep(pagePermissions),
  };
};

export const setCurrentRolePageFeaturePermission = (
  pageId: string,
  featureId: string,
  value: boolean
) => {
  return {
    type: roleActionTypes.SET_CURRENT_ROLE_PAGE_FEATURE_PERMISSIONS,
    payload: {
      pageId,
      featureId,
      value,
    },
  };
};

export const setCurrentRolePageFeatureActionPermission = (
  pageId: string,
  featureId: string,
  action: string,
  value: boolean
) => {
  return {
    type: roleActionTypes.SET_CURRENT_ROLE_PAGE_FEATURE_ACTION_PERMISSIONS,
    payload: {
      pageId,
      featureId,
      action,
      value,
    },
  };
};

export const setCurrentRolePageGroupFeatureActionPermissions = (
  pageId: string,
  featureInstanceKey: string,
  value: boolean
) => {
  return {
    type: roleActionTypes.SET_CURRENT_ROLE_PAGE_GROUP_FEATURE_ACTION_PERMISSIONS,
    payload: {
      pageId,
      featureInstanceKey,
      value,
    },
  };
};

export const setCurrentRolePermissionIsSaving = (isSaving: boolean) => {
  return {
    type: roleActionTypes.SET_CURRENT_ROLE_PERMISSIONS_IS_SAVING,
    payload: {
      isSaving,
    },
  };
};
